@let maintenance = (maintenance$ | async) ?? [];
@let statuses = (statuses$ | async) ?? [];
@let maintenanceTypes = (maintenanceTypes$ | async) ?? [];
@let warrantyTypes = (warrantyTypes$ | async) ?? [];
@let isMaintenanceLoading = maintenanceLoadingStatus() === LoadingState.LOADING;
@let isMaintenanceLoaded = maintenanceLoadingStatus() === LoadingState.READY;

<section class="page-content page-viewport">
  <mon-page-toolbar
    titleTranslateKey="monitoring.menu.maintenance"
    [searchControl]="keywordControl"
    searchPlaceholderTranslateKey="monitoring.search"
    [actions]="toolbarActions"
    [selectedFiltersCount]="selectedFiltersCount"
    (actionClick)="onToolbarAction($event)"
  >
    <div
      toolbarInlineControls
      class="toolbar-date-picker--icon-only"
      [formGroup]="maintenanceFormFilters"
    >
      <lib-date-picker
        class="maintenance__filter"
        [placeholder]="'monitoring.select-date' | translate"
        [minDate]="minDateAvailable"
        [maxDate]="maxDateAvailable"
        mode="range"
        formControlName="datePeriod"
      />
    </div>

    <div
      toolbarFilters
      class="maintenance__filters"
      [formGroup]="maintenanceFormFilters"
    >
      <lib-multiselect
        class="maintenance__filter"
        titleTranslationKey="monitoring.maintenance-status"
        chosenTranslationKey="monitoring.maintenance-statuses-chosen"
        [options]="statuses"
        [formControl]="getMaintenanceFiltersControl('statusIds')"
      />

      <lib-multiselect
        class="maintenance__filter"
        titleTranslationKey="monitoring.maintenance-type"
        chosenTranslationKey="monitoring.maintenance-types-chosen"
        [options]="maintenanceTypes"
        [formControl]="getMaintenanceFiltersControl('maintenanceTypeIds')"
      />

      @if (canReadFacilities()) {
        <mon-facility-selector
          class="maintenance__filter"
          [controlEntity]="facilityIdsControl"
          (selectionChange)="onFacilitySelectionChange($event)"
        />
      }

      <mon-service-center-selector
        class="maintenance__filter"
        [controlEntity]="serviceCenterIdsControl"
        (selectionChange)="onServiceCenterSelectionChange($event)"
      />

      <lib-multiselect
        class="maintenance__filter"
        titleTranslationKey="monitoring.maintenance-warranty"
        chosenTranslationKey="monitoring.maintenance-warranties-chosen"
        [options]="warrantyTypes"
        [formControl]="getMaintenanceFiltersControl('warrantyTypeIds')"
      />
    </div>

    @if (isFiltersActive) {
      <div
        toolbarSelectedFilters
        class="maintenance__selected-filters"
      >
        <lib-button
          (click)="clearAllFilters()"
          class="chip chip--button"
          buttonStyle="danger"
          buttonSize="medium"
          buttonType="button"
        >
          <span> {{ 'monitoring.clear-filters' | translate }} </span>
        </lib-button>

        @for (filter of allSelectedFilters; track filter[0]) {
          @for (selectedFilter of filter[1]; track selectedFilter.id) {
            <lib-button
              (click)="deselectFilter(filter[0], selectedFilter)"
              class="chip chip--button"
              buttonStyle="danger"
              buttonSize="medium"
              buttonType="button"
            >
              <lib-svg-icon
                icon="close"
                size="16"
              />
              <span> {{ selectedFilter.name }} </span>
            </lib-button>
          }
        }
      </div>
    }
  </mon-page-toolbar>

  <section class="page-content page-table page-table-scroll">
    <div class="page-table-scroll__content">
      <lib-table
        class="page-table-scroll__table maintenance-table"
        trCustomClass="desktop-table-row--hoverable"
        [columns]="columns"
        [dataSource]="maintenance"
        [cellTemplates]="{
          createdDate: createdDateTpl,
          status: statusTpl,
          maintenanceType: maintenanceTypeTpl,
          facilityName: facilityTpl,
          serviceCenterName: serviceCenterTpl,
          warrantyType: warrantyTypeTpl,
          error: errorTpl,
        }"
        [loaded]="isMaintenanceLoaded"
        [loading]="isMaintenanceLoading"
        (desktopRowClick)="openDeviceCard($event)"
      />
    </div>

    @if (!(maintenance.length === 0 && currentPage() === 1)) {
      <lib-pagination
        (paginationChange)="onPageChange($event)"
        [loading]="isMaintenanceLoading"
        [totalCount]="maintenanceTotalCount()"
        [pageIndex]="currentPage()"
        [pageSize]="100"
      />
    }
  </section>

  <ng-template
    #createdDateTpl
    let-row="data"
  >
    <div class="maintenance__cell">
      <div>{{ asMaintenance(row).createdDate | date: 'yyyy.MM.dd' : 'UTC' }}</div>
      <div class="maintenance__cell-time">
        {{ asMaintenance(row).createdDate | date: 'HH:mm:ss' : 'UTC' }} (UTC)
      </div>
    </div>
  </ng-template>

  <ng-template
    #statusTpl
    let-row="data"
  >
    <div class="maintenance__cell">
      <span
        class="maintenance__status-chip status-chip"
        [class]="'maintenance__status-chip--' + asMaintenance(row).status"
      >
        {{ displayStatus(asMaintenance(row).status) | translate }}
      </span>
    </div>
  </ng-template>

  <ng-template
    #maintenanceTypeTpl
    let-row="data"
  >
    <div class="maintenance__cell">
      {{ displayMaintenanceType(asMaintenance(row).maintenanceType) | translate }}
    </div>
  </ng-template>

  <ng-template
    #facilityTpl
    let-row="data"
  >
    <div class="maintenance__cell">
      <div class="maintenance__cell-facility">{{ asMaintenance(row).facilityName || '-' }}</div>
    </div>
  </ng-template>

  <ng-template
    #serviceCenterTpl
    let-row="data"
  >
    <div class="maintenance__cell">{{ asMaintenance(row).serviceCenterName || '-' }}</div>
  </ng-template>

  <ng-template
    #warrantyTypeTpl
    let-row="data"
  >
    <div class="maintenance__cell">
      {{ displayWarrantyType(asMaintenance(row).warrantyType) | translate }}
    </div>
  </ng-template>

  <ng-template
    #errorTpl
    let-row="data"
  >
    <div class="maintenance__cell">{{ displayError(asMaintenance(row).error) | translate }}</div>
  </ng-template>
</section>

<router-outlet />
